import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { useSearchParams } from 'react-router-dom';
import Loader from '../../../components/loader';
import InlineAlert from '../../../components/inlineAlert';
import { FilterSelect } from '../../../components/filterControls';
import { filterBarSx } from '../../../styles/filterStyles';
import { formatFull, vizInk } from '../../../styles/analyticsStyles';
import { CoverageTile, CropsGrown, LocationBreakdown, StatTile, sourceLabels } from './charts';
import { getAnalysisSheet, listAnalyticsCountries } from './countryAnalysis.service';

// Analytics > Country Analysis. A printable sheet per scope: how far the footprint reaches (states,
// districts and blocks covered), how many exchanges are established and where, the farmer reach and
// acreage behind them, and the crops grown — for corporate market collateral.
//
// The sheet drills. Opening a row on the country sheet re-asks every one of those questions about
// that state and lists its districts; opening a district lists its blocks, and so on to the bottom
// of whatever hierarchy the country defines. Both the server and this screen treat that as one view
// with a scope rather than a view per level — the panels below never learn how deep they are.
//
// PDF is the browser's own print pipeline (window.print against the print stylesheet below) rather
// than a bundled rasteriser. That keeps the page dependency-free, and the output is real vector text
// — selectable, searchable, and sharp when the deck is projected or printed. The trade-off is that
// the browser shows its print dialog, where the user picks "Save as PDF" as the destination.

const PRINT_STYLE_ID = 'country-analysis-print-style';

// Scoped to this sheet via .analytics-sheet so nothing here leaks into printing other screens.
const printCss = `
@media print {
  @page { size: A4 portrait; margin: 12mm; }
  body * { visibility: hidden !important; }
  .analytics-sheet, .analytics-sheet * { visibility: visible !important; }
  .analytics-sheet {
    position: absolute !important;
    inset: 0 auto auto 0 !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  /* The controls are the one thing that must not print: a picker and a button are meaningless on
     paper, and they would push the sheet past its first page. Drill affordances go with them — the
     breadcrumb still prints, because it is what tells a reader whose numbers these are. */
  .analytics-no-print { display: none !important; }
  /* Keep each panel whole rather than letting a bar chart split across the page break. */
  .analytics-panel { break-inside: avoid; page-break-inside: avoid; }
  .analytics-sheet { font-size: 11px; }
}
`;

/**
 * The print rules are injected on mount instead of living in a global stylesheet: this is the only
 * screen that prints, and a global `body * { visibility: hidden }` would otherwise be one stray
 * import away from breaking printing everywhere else in the app.
 */
function usePrintStyles() {
  useEffect(() => {
    if (document.getElementById(PRINT_STYLE_ID)) return undefined;
    const style = document.createElement('style');
    style.id = PRINT_STYLE_ID;
    style.textContent = printCss;
    document.head.appendChild(style);
    return () => {
      document.getElementById(PRINT_STYLE_ID)?.remove();
    };
  }, []);
}

export default function CountryAnalysis() {
  usePrintStyles();

  // The scope lives in the URL rather than in component state so the browser's own Back button walks
  // back up the hierarchy — which is the first thing anyone tries after clicking three levels deep —
  // and so a district sheet can be linked to. Nothing else is kept about the trail: the response
  // carries its own breadcrumb, so a pasted link renders in full without a prior visit.
  const [searchParams, setSearchParams] = useSearchParams();
  const countryId = searchParams.get('country') || '';
  const locationId = searchParams.get('location') || '';

  const [countries, setCountries] = useState([]);
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const goTo = useCallback(
    (nextCountryId, nextLocationId, replace = false) => {
      const params = {};
      if (nextCountryId) params.country = nextCountryId;
      if (nextLocationId) params.location = nextLocationId;
      setSearchParams(params, { replace });
    },
    [setSearchParams]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const res = await listAnalyticsCountries();
      if (cancelled) return;
      setLoading(false);
      if (res.statusCode !== 200) {
        setError(res.message);
        return;
      }
      const list = res.data || [];
      setCountries(list);
      // Open on the busiest market rather than an empty picker — the list arrives sorted by user
      // count, so the first entry is the one most likely to be presented. Replaced rather than
      // pushed, so Back from the first view leaves the screen instead of returning to a blank one.
      if (!countryId && list.length) goTo(list[0].countryId, '', true);
    })();
    return () => {
      cancelled = true;
    };
    // Runs once: the picker is loaded on mount, and re-running it on navigation would re-seed the
    // country and fight the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!countryId) return undefined;
    let cancelled = false;
    (async () => {
      // The request resolves from memory when this scope was visited in the last few minutes, so
      // the loading state is set only once we know we are actually waiting on the network. Setting
      // it first would dim the sheet for a frame on a navigation that is instant.
      const pending = getAnalysisSheet(countryId, locationId);
      let settled = false;
      pending.then(() => {
        settled = true;
      });
      // A microtask is enough to tell a cached answer from a real request.
      await Promise.resolve();
      if (!settled && !cancelled) setLoading(true);

      const res = await pending;
      if (cancelled) return;
      setLoading(false);
      if (res.statusCode !== 200) {
        setError(res.message);
        setSheet(null);
        return;
      }
      setError('');
      setSheet(res.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [countryId, locationId]);

  const countryOptions = useMemo(
    () =>
      countries.map((country) => ({
        value: country.countryId,
        label: `${country.countryName} (${formatFull(country.totalUsers)})`,
      })),
    [countries]
  );

  const generatedOn = useMemo(
    () =>
      new Date().toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
    [sheet]
  );

  const reach = sheet?.reach;
  const areaUnit = reach?.acreUnit || 'acres';
  const path = sheet?.path || [];
  const scopeName = sheet?.locationName || sheet?.countryName || '';
  // Everything on a drilled sheet is counted inside the scope, and the panels say "covered" without
  // saying "covered where". The suffix supplies it once, in the places a reader would otherwise
  // assume national figures.
  const within = sheet?.locationId ? ` in ${scopeName}` : '';

  return (
    <>
      <Box className="analytics-no-print" sx={{ ...filterBarSx, justifyContent: 'space-between' }}>
        <Box>
          <Typography
            className="app-page-title"
            sx={{ fontSize: '34px', lineHeight: '40px', letterSpacing: '-0.25px', fontWeight: 700, color: '#1C1B1F' }}
          >
            Country Analysis
          </Typography>
          <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>
            Market footprint: coverage, exchanges established, reach and crops. Open a row to go a
            level deeper.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <FilterSelect
            value={countryId}
            // Switching country abandons the trail: a district id means nothing in another country,
            // and the server would answer it with a 404.
            onChange={(value) => goTo(value, '')}
            placeholder="Select country"
            options={countryOptions}
            minWidth={220}
          />
          <Button
            variant="contained"
            onClick={() => window.print()}
            disabled={!sheet}
            startIcon={<Icon icon="material-symbols:picture-as-pdf-outline-rounded" width="18" height="18" />}
            sx={{
              height: 44,
              px: 2.5,
              borderRadius: '999px',
              textTransform: 'none',
              fontSize: 14,
              fontWeight: 600,
              boxShadow: 'none',
              bgcolor: '#4F8506',
              '&:hover': { bgcolor: '#4F8506', boxShadow: 'none' },
            }}
          >
            Download PDF
          </Button>
        </Box>
      </Box>

      <Box className="analytics-no-print">
        <InlineAlert message={error} onClose={() => setError('')} variant="error" />
      </Box>

      {sheet ? (
        <Box
          className="analytics-sheet"
          sx={{
            backgroundColor: vizInk.surface,
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
            // Hold the previous render while the next level loads rather than flashing a skeleton,
            // so the sheet never jumps.
            opacity: loading ? 0.6 : 1,
            transition: 'opacity 120ms ease',
          }}
        >
          <Box
            className="analytics-panel"
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{ fontSize: 11, letterSpacing: '1.4px', fontWeight: 700, textTransform: 'uppercase', color: '#4F8506' }}
              >
                eKutir &middot; {sheet.levelLabel ? `${sheet.levelLabel} footprint` : 'Country market footprint'}
              </Typography>
              <Typography sx={{ fontSize: 28, lineHeight: '34px', fontWeight: 700, color: vizInk.primary }}>
                {scopeName}
              </Typography>
              {path.length ? (
                <Breadcrumb
                  countryName={sheet.countryName}
                  path={path}
                  onOpen={(id) => goTo(countryId, id)}
                />
              ) : null}
            </Box>
            <Typography sx={{ fontSize: 11, lineHeight: '16px', color: vizInk.muted, textAlign: 'right' }}>
              Generated {generatedOn}
              <br />
              {formatFull(sheet.exchanges)} exchange{sheet.exchanges === 1 ? '' : 's'} established
              {within}
            </Typography>
          </Box>

          {sheet.coverage?.length ? (
            <Box className="analytics-panel" sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {sheet.coverage.map((level) => (
                <CoverageTile
                  key={level.level}
                  label={`${level.label} covered`}
                  covered={level.covered}
                  total={level.total}
                />
              ))}
            </Box>
          ) : null}

          <Box className="analytics-panel" sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <StatTile
              label="Potential farmer reach"
              value={reach?.potentialFarmers}
              source={reach?.potentialFarmersSource}
              hint="farmers inside the covered footprint"
            />
            <StatTile
              label="Potential acreage"
              value={reach?.potentialAcres}
              unit={areaUnit}
              source={reach?.potentialAcresSource}
              hint="cultivable land inside the covered footprint"
            />
            <StatTile
              label="Farmers onboarded"
              value={sheet.farmersOnboarded}
              hint="registered on the platform to date"
            />
          </Box>

          {sheet.rowLabel ? (
            <Box className="analytics-panel">
              <LocationBreakdown
                rows={sheet.rows}
                rowLabel={sheet.rowLabel}
                coverageLabel={sheet.rowCoverageLabel}
                areaUnit={areaUnit}
                onOpen={(row) => goTo(countryId, row.locationId)}
              />
            </Box>
          ) : null}

          <Box className="analytics-panel">
            <CropsGrown crops={sheet.crops} source={sheet.cropsSource} blockLabel={sheet.blockLabel} />
          </Box>

          <Typography sx={{ fontSize: 10, lineHeight: '14px', color: vizInk.muted }}>
            An exchange is a DARE — eKutir&apos;s block-level rural enterprise. A tier counts as covered when it
            holds a user, a farmer, or an exchange&apos;s block record, and only areas that hold one of those
            are listed. Potential reach and acreage are what the exchanges themselves declare; where none has,
            {' '}
            {(sourceLabels.PARTNERSHIP_TARGETS || '').toLowerCase()} is used instead and the figure says so.
            Counts exclude deleted users.
            {sheet.unplacedExchanges > 0 ? (
              <>
                {' '}
                {formatFull(sheet.unplacedExchanges)} exchange
                {sheet.unplacedExchanges === 1 ? ' is' : 's are'} registered against a location that does not
                resolve to a row, so {sheet.unplacedExchanges === 1 ? 'it is' : 'they are'} counted in the
                header total but appear in no row above.
              </>
            ) : null}
          </Typography>
        </Box>
      ) : null}

      {loading && !sheet ? <Loader /> : null}
    </>
  );
}

/**
 * The trail back up. It prints, unlike the rest of the drill chrome: on paper a district sheet
 * headed "Cuttack" is ambiguous, and the trail is the only thing that says which Cuttack.
 *
 * The last entry is the scope itself and is deliberately inert — a link to the page you are on is
 * noise, and rendering it as one invites the click that does nothing.
 */
function Breadcrumb({ countryName, path, onOpen }) {
  const steps = [{ locationId: '', locationName: countryName }, ...path];

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
      {steps.map((step, index) => {
        const last = index === steps.length - 1;
        return (
          <Box key={step.locationId || 'country'} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {index ? (
              <Typography component="span" sx={{ fontSize: 12, lineHeight: '16px', color: vizInk.muted }}>
                &rsaquo;
              </Typography>
            ) : null}
            <Typography
              component="span"
              {...(last
                ? null
                : {
                    role: 'button',
                    tabIndex: 0,
                    onClick: () => onOpen(step.locationId),
                    onKeyDown: (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onOpen(step.locationId);
                      }
                    },
                  })}
              sx={{
                fontSize: 12,
                lineHeight: '16px',
                color: last ? vizInk.secondary : '#4F8506',
                fontWeight: last ? 600 : 500,
                cursor: last ? 'default' : 'pointer',
                '&:hover': last ? null : { textDecoration: 'underline' },
              }}
            >
              {step.locationName}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
